import React, { useEffect, useRef } from 'react';
import styles from './LazyVideo.module.scss';
import { useSelector } from 'react-redux';

const LazyVideo = ({
  src,
  isPaused = false,
  isLooped = true,
  cc = 'video',
  style,
  controls = false,
  preload = 'metadata',
}) => {
  const videoRef = useRef(null);
  const awaitingUnlockRef = useRef(false);
  const cleanupUnlockRef = useRef(() => {});
  const volumeState = useSelector((state) => state.volume);

  const shouldBeMuted = volumeState !== 'on';

  const removeUnlockListeners = () => {
    window.removeEventListener('pointerdown', onUserUnlock, { capture: true });
    window.removeEventListener('keydown', onUserUnlock, { capture: true });
  };

  const onUserUnlock = () => {
    const v = videoRef.current;
    if (!v) return removeUnlockListeners();
    if (!awaitingUnlockRef.current) return removeUnlockListeners();

    awaitingUnlockRef.current = false;
    v.muted = false;
    v.removeAttribute('muted');
    v.play().finally(removeUnlockListeners);
  };

  const addUnlockListeners = () => {
    removeUnlockListeners();
    awaitingUnlockRef.current = true;
    window.addEventListener('pointerdown', onUserUnlock, {
      once: true,
      capture: true,
    });
    window.addEventListener('keydown', onUserUnlock, {
      once: true,
      capture: true,
    });
    cleanupUnlockRef.current = removeUnlockListeners;
  };

  const ensurePlayingMuted = async () => {
    const v = videoRef.current;
    if (!v) return;
    try {
      v.muted = true;
      v.setAttribute('muted', '');
      await v.play();
    } catch {
      console.log('');
    }
  };

  const attemptUnmute = async () => {
    const v = videoRef.current;
    if (!v) return;
    try {
      v.muted = false;
      v.removeAttribute('muted');
      await v.play();
    } catch {
      addUnlockListeners();
    }
  };

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    v.playsInline = true;
    v.setAttribute('playsinline', '');
    v.setAttribute('webkit-playsinline', '');

    v.muted = true;
    v.setAttribute('muted', '');

    if (isPaused) {
      v.pause();
      return;
    }

    if (v.readyState >= 2) {
      ensurePlayingMuted().then(() => {
        if (!shouldBeMuted) attemptUnmute();
      });
    } else {
      const onCanPlay = () => {
        v.removeEventListener('canplay', onCanPlay);
        if (!isPaused) {
          ensurePlayingMuted().then(() => {
            if (!shouldBeMuted) attemptUnmute();
          });
        }
      };
      v.addEventListener('canplay', onCanPlay);
      return () => v.removeEventListener('canplay', onCanPlay);
    }
  }, [src, isPaused]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    if (shouldBeMuted) {
      v.muted = true;
      v.setAttribute('muted', '');
      removeUnlockListeners();
      awaitingUnlockRef.current = false;
    } else {
      attemptUnmute();
    }
  }, [shouldBeMuted]);

  useEffect(() => () => cleanupUnlockRef.current(), []);

  return (
    <video
      key={src}
      ref={videoRef}
      className={`${styles[cc]} ${!controls ? styles.videoControlsHidden : ''}`}
      src={src}
      autoPlay
      loop={isLooped}
      muted
      playsInline
      style={style}
      controls={controls}
      preload={preload}
      crossOrigin="anonymous"
    />
  );
};

export default LazyVideo;
